import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Users, Plus, Trash2, Loader2, Search, Pencil, X, Check, Building2, ChevronRight,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import EmptyState from "../components/EmptyState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", gstin: "", address: "", email: "", phone: ""
  });

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    try {
      const params = search ? { search } : {};
      const res = await axios.get(`${API}/customers/`, { params, withCredentials: true });
      setCustomers(res.data.customers || []);
    } catch { toast.error("Failed to load customers"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchCustomers(), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const resetForm = () => {
    setForm({ name: "", gstin: "", address: "", email: "", phone: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Customer name required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/customers/${editingId}`, form, { withCredentials: true });
        toast.success("Customer updated");
      } else {
        await axios.post(`${API}/customers/`, form, { withCredentials: true });
        toast.success("Customer added");
      }
      resetForm();
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleEdit = (c) => {
    setForm({ name: c.name, gstin: c.gstin || "", address: c.address || "", email: c.email || "", phone: c.phone || "" });
    setEditingId(c.customer_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try {
      await axios.delete(`${API}/customers/${id}`, { withCredentials: true });
      toast.success("Customer deleted");
      fetchCustomers();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="customers-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Customers</h1>
            <p className="text-sm text-slate-500">Manage your customer and vendor directory</p>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-700 text-white text-sm" data-testid="add-customer-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Customer
          </Button>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="customer-form">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{editingId ? "Edit" : "Add"} Customer</h2>
                <button onClick={resetForm} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Acme Corp" data-testid="field-name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">GSTIN</label>
                  <Input value={form.gstin} onChange={e => setForm(p => ({ ...p, gstin: e.target.value.toUpperCase() }))} placeholder="29AABCT1234A1Z5" className="font-mono uppercase" maxLength={15} data-testid="field-gstin" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="email@company.com" data-testid="field-email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+91 98765 43210" data-testid="field-phone" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                  <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} placeholder="123 Business Park, Mumbai" data-testid="field-address" />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-customer-form-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                  {editingId ? "Update" : "Add Customer"}
                </Button>
                <Button variant="ghost" onClick={resetForm}>Cancel</Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, GSTIN, or email..." className="pl-10" data-testid="search-customers" />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : customers.length === 0 ? (
          <EmptyState
            icon={Users}
            iconBg="bg-blue-50 text-blue-600"
            title="No customers yet"
            description="Save customer details once — they auto-fill on every new invoice with correct GSTIN & state."
            bullets={[
              "GSTIN validation + state auto-detect for IGST/CGST split",
              "Email & phone saved for instant invoice sharing",
              "One-click reuse from the invoice builder",
            ]}
            primaryAction={{ label: "Add First Customer", icon: Plus, onClick: () => { resetForm(); setShowForm(true); } }}
            testId="customers-empty-state"
          />
        ) : (
          <div className="space-y-2">
            {customers.map(c => (
              <motion.div key={c.customer_id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all group" data-testid={`cust-row-${c.customer_id}`}>
                <div className="flex items-center gap-4 p-4">
                  <Link to={`/customers/${c.customer_id}`} className="flex items-center gap-4 flex-1 min-w-0" data-testid={`cust-open-${c.customer_id}`}>
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-slate-900 text-sm">{c.name}</span>
                        {c.gstin && <span className="text-xs font-mono px-2 py-0.5 bg-slate-100 text-slate-600 rounded">{c.gstin}</span>}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        {c.state && <span>{c.state}</span>}
                        {c.email && <span>{c.email}</span>}
                        {c.phone && <span>{c.phone}</span>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                  </Link>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleEdit(c)} className="p-1.5 text-slate-300 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-${c.customer_id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(c.customer_id)} className="p-1.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-${c.customer_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
